import {
  alpha,
  createTheme,
  Direction,
  Theme,
  ThemeOptions,
} from "@mui/material";
import { THEMES } from "utils/constants";
import {
  error,
  info,
  primary,
  secondary,
  success,
  warning,
} from "./themeColors";

const fontSize = 14;

const lightPalette = {
  mode: "light" as const,
  primary,
  secondary,
  error,
  warning,
  success,
  info,
  divider: "#D9E2EE",
  background: {
    default: "#F5F8FC",
    paper: "#FFFFFF",
  },
  text: {
    primary: "#162031",
    secondary: "#445168",
    disabled: "#6B7890",
  },
};

const darkPalette = {
  mode: "dark" as const,
  primary: {
    ...primary,
    main: "#5FA8FF",
    light: "#A8D2FF",
    dark: "#3A8CF0",
  },
  secondary: {
    ...secondary,
    100: "#111A27",
    200: "#1A2638",
    300: "#243347",
    400: "#A6B5C9",
    500: "#E1E8F3",
    main: "#E1E8F3",
    light: "#0F1622",
  },
  error,
  warning,
  success,
  info,
  divider: "#2A3A51",
  background: {
    default: "#0D1420",
    paper: "#131D2C",
  },
  text: {
    primary: "#EEF3FB",
    secondary: "#B8C5D8",
    disabled: "#8A99AE",
  },
};

const getBaseOptions = (mode: string): ThemeOptions => {
  const isDark = mode === THEMES.DARK;
  const palette = isDark ? darkPalette : lightPalette;

  return {
    direction: "ltr",
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1920,
      },
    },
    shape: {
      borderRadius: 14,
    },
    spacing: 8,
    palette,
    components: {
      MuiAvatar: {
        styleOverrides: {
          fallback: {
            height: "75%",
            width: "75%",
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          "*": {
            boxSizing: "border-box",
          },
          html: {
            MozOsxFontSmoothing: "grayscale",
            WebkitFontSmoothing: "antialiased",
            height: "100%",
            width: "100%",
          },
          body: {
            height: "100%",
            backgroundColor: palette.background.default,
            color: palette.text.primary,
          },
          a: {
            textDecoration: "none",
            color: "inherit",
          },
          "#root": {
            height: "100%",
          },
          "#nprogress .bar": {
            zIndex: "9999 !important",
            backgroundColor: `${palette.primary.main} !important`,
            width: "100%",
            position: "fixed",
          },
          "input[type=number]::-webkit-outer-spin-button, input[type=number]::-webkit-inner-spin-button":
            {
              WebkitAppearance: "none",
              margin: 0,
            },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderRadius: 16,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: isDark
              ? "0px 18px 40px rgba(0, 0, 0, 0.28)"
              : "0px 12px 30px rgba(23, 43, 77, 0.08)",
            border: `1px solid ${palette.divider}`,
            borderRadius: 16,
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            fontFamily: "'Montserrat', sans-serif",
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 12,
            fontWeight: 700,
            padding: "0.6rem 1.25rem",
          },
          outlinedPrimary: {
            borderColor: palette.primary.main,
            color: palette.primary.main,
          },
          containedPrimary: {
            color: "#fff",
            "&:hover": {
              backgroundColor: palette.primary.dark,
              boxShadow: "none",
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: palette.text.secondary,
            fontWeight: 500,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& input::placeholder": {
              color: palette.text.secondary,
              opacity: 1,
            },
            "& .MuiInputBase-input": {
              color: palette.text.primary,
              fontWeight: 500,
            },
            "& .MuiOutlinedInput-root": {
              borderRadius: 14,
              backgroundColor: palette.background.paper,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: palette.divider,
                borderWidth: 2,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: palette.primary.main,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: palette.primary.main,
              },
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: 2,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            color: palette.text.primary,
            fontWeight: 500,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontWeight: 600,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: palette.divider,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: palette.text.secondary,
            textTransform: "none",
            fontSize: 12,
            fontWeight: 700,
            minWidth: "auto",
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: 3,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          root: {
            "& .MuiPopover-paper": {
              boxShadow: "none",
              borderRadius: "14px",
              border: `1px solid ${palette.divider}`,
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            border: `1px solid ${palette.divider}`,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            paddingBlock: 10,
            "&:hover": {
              backgroundColor: alpha(palette.primary.main, isDark ? 0.2 : 0.08),
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: palette.divider,
            color: palette.text.secondary,
          },
          head: {
            color: palette.text.secondary,
            fontWeight: 700,
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: isDark
              ? alpha("#5A708D", 0.3)
              : alpha("#94A4C4", 0.25),
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            "&:hover": { backgroundColor: alpha(palette.primary.main, 0.12) },
          },
        },
      },
    },
    typography: {
      button: {
        fontWeight: 700,
      },
      fontFamily: "'Montserrat', sans-serif",
      h1: {
        fontWeight: 800,
        fontSize: "4.25rem",
      },
      h2: {
        fontWeight: 700,
        fontSize: "3.25rem",
      },
      h3: {
        fontWeight: 700,
        fontSize: "2.1rem",
      },
      h4: {
        fontWeight: 700,
        fontSize: "1.75rem",
      },
      h5: {
        fontWeight: 700,
        fontSize: "1.35rem",
      },
      h6: {
        fontWeight: 700,
        fontSize,
      },
      overline: {
        fontWeight: 700,
      },
      body1: { fontSize },
      body2: { fontSize },
    },
  };
};

export type themeSettingsProps = {
  theme: string;
  direction: Direction;
  responsiveFontSizes: boolean;
};

export const ukoTheme = (settings?: themeSettingsProps) => {
  const selectedMode =
    settings?.theme === THEMES.DARK ? THEMES.DARK : THEMES.LIGHT;
  const options = getBaseOptions(selectedMode);

  const theme: Theme = createTheme({
    ...options,
    direction: settings?.direction || "ltr",
  });

  theme.shadows[1] =
    selectedMode === THEMES.DARK
      ? "0px 4px 18px rgba(0, 0, 0, 0.3)"
      : "0px 4px 23px rgba(0, 0, 0, 0.08)";
  theme.shadows[2] =
    selectedMode === THEMES.DARK
      ? "0px 10px 24px rgba(0, 0, 0, 0.35)"
      : "0px 0px 21px 1px rgba(0, 0, 0, 0.06)";
  theme.shadows[3] =
    selectedMode === THEMES.DARK
      ? "0px 16px 36px rgba(0, 0, 0, 0.4)"
      : "0px 10px 30px rgba(0, 0, 0, 0.08)";
  theme.shadows[4] =
    selectedMode === THEMES.DARK
      ? "0px 18px 42px rgba(0, 0, 0, 0.45)"
      : "0px 7px 30px 3px rgba(0, 0, 0, 0.05)";

  return theme;
};
